// View + edit the permissions of a role. System roles are read-only (chips only);
// custom roles get a toggle grid grouped into an Area → Domain tree with a Save
// button. The full permission catalogue is loaded so unassigned permissions can
// be added. Area and sub-domain are derived from each key (see lib/grouping). A
// sticky filter bar (area chips + text search) narrows the picker without ever
// touching the pending selection, which is tracked by permission id independently
// of what the tree renders.

import { useEffect, useMemo, useState } from "react"
import { adminApi } from "../api/endpoints.js"
import type { PermissionDto, RoleDetailDto } from "../api/types.js"
import { useWritesEnabled } from "../auth/AdminAuthContext.js"
import { fr } from "../i18n/fr.js"
import { messageForError } from "../lib/errors.js"
import { areaLabel, domainLabel, groupByAreaAndDomain, selectionChanged } from "../lib/grouping.js"
import { useAsync } from "../lib/useAsync.js"
import { Drawer } from "../ui/Drawer.js"
import { Alert, Button, Chip, Spinner, ToggleChip } from "../ui/primitives.js"
import { useNotify } from "../ui/toast.js"
import { PermissionFilterBar, usePermissionFilter } from "./PermissionFilterBar.js"

export function RolePermissionsDrawer({
  roleId,
  onClose,
  onSaved
}: { roleId: string; onClose: () => void; onSaved: () => void }) {
  const notify = useNotify()
  const writesEnabled = useWritesEnabled()

  const role = useAsync<RoleDetailDto>(() => adminApi.getRole(roleId), [roleId])
  const catalogue = useAsync<PermissionDto[]>(() => adminApi.listPermissions(), [])

  const editable = writesEnabled && role.data !== null && !role.data.isSystem

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)

  // Seed the selection from the role's current permissions once loaded.
  useEffect(() => {
    if (role.data) setSelected(new Set(role.data.permissions.map((p) => p.id)))
  }, [role.data])

  const areas = useMemo(() => groupByAreaAndDomain(catalogue.data ?? []), [catalogue.data])
  const filter = usePermissionFilter(areas)
  const currentIds = useMemo(() => role.data?.permissions.map((p) => p.id) ?? [], [role.data])
  const dirty = selectionChanged(currentIds, selected)

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  async function save() {
    if (!role.data) return
    setSaving(true)
    try {
      await adminApi.setRolePermissions(role.data.id, [...selected])
      notify.success(fr.app.save)
      onSaved()
      onClose()
    } catch (err) {
      notify.error(messageForError(err, fr.app.genericError))
    } finally {
      setSaving(false)
    }
  }

  const footer = editable ? (
    <>
      <Button variant="ghost" onClick={onClose} disabled={saving}>
        {fr.app.cancel}
      </Button>
      <Button variant="primary" onClick={save} disabled={!dirty || saving}>
        {fr.roles.permissions.save}
      </Button>
    </>
  ) : undefined

  return (
    <Drawer
      open
      title={fr.roles.permissions.drawerTitle}
      subtitle={role.data ? role.data.displayName || role.data.name : undefined}
      onClose={onClose}
      footer={footer}
    >
      {role.loading || catalogue.loading ? <Spinner label={fr.roles.permissions.loading} /> : null}
      {role.error ? <Alert variant="error">{fr.roles.permissions.loadFailed}</Alert> : null}

      {role.data && !editable ? <ReadOnlyPermissions permissions={role.data.permissions} /> : null}

      {role.data && editable ? (
        <div className="stack">
          <p className="muted" style={{ margin: 0 }}>
            {fr.roles.permissions.assignTitle}
          </p>
          <PermissionFilterBar
            areaKeys={filter.areaKeys}
            area={filter.area}
            onAreaChange={filter.setArea}
            query={filter.query}
            onQueryChange={filter.setQuery}
            onSurface
          />
          {filter.filtered.length === 0 ? <Alert>{fr.permissions.filter.empty}</Alert> : null}
          {filter.filtered.map((area) => (
            <div key={area.area}>
              <div className="area-section__title">{areaLabel(area.area, fr.permissions.area)}</div>
              {area.domains.map((group) => (
                <div key={group.domain} style={{ marginBottom: 8 }}>
                  <div className="field__label" style={{ marginBottom: 8 }}>
                    {domainLabel(group.domain, fr.permissions.domain)}
                  </div>
                  <div className="row">
                    {group.permissions.map((p) => (
                      <ToggleChip
                        key={p.id}
                        selected={selected.has(p.id)}
                        onToggle={() => toggle(p.id)}
                        title={p.description ?? undefined}
                      >
                        {p.key}
                      </ToggleChip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}

      {role.data?.isSystem ? (
        <div style={{ marginTop: 16 }}>
          <Alert>{fr.roles.permissions.systemReadonly}</Alert>
        </div>
      ) : null}
    </Drawer>
  )
}

function ReadOnlyPermissions({ permissions }: { permissions: PermissionDto[] }) {
  const areas = groupByAreaAndDomain(permissions)
  if (areas.length === 0) return <p className="muted">{fr.roles.permissions.empty}</p>
  return (
    <div className="stack">
      {areas.map((area) => (
        <div key={area.area}>
          <div className="area-section__title">{areaLabel(area.area, fr.permissions.area)}</div>
          {area.domains.map((group) => (
            <div key={group.domain} style={{ marginBottom: 8 }}>
              <div className="field__label" style={{ marginBottom: 8 }}>
                {domainLabel(group.domain, fr.permissions.domain)}
              </div>
              <div className="row">
                {group.permissions.map((p) => (
                  <Chip key={p.id} mono title={p.description ?? undefined}>
                    {p.key}
                  </Chip>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
